import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Header } from 'pages/HomePublic/components/Header';
import { StepIndicator } from 'components/ui/StepIndicator';
import Attachment from 'components/ui/Attachment';
import Participants from 'components/ui/Participants';
import ModalWarning from 'components/ui/ModalWarning';
import { useModal } from 'context/ModalContext/Modal.context';
import { useAuth } from 'context/AuthContext';
import { useRule } from 'hooks/useRule';
import { BsCameraVideoFill } from 'react-icons/bs';
import { HiUsers } from 'react-icons/hi';
import { FaBell } from "react-icons/fa6";
import { IoCheckmarkCircle } from 'react-icons/io5';
import { atualizarIniciativa, inscricaoIniciativaPremio } from 'services/iniciativa-premio.service';
import { registrarConfirmar, registrarEnviarVideo } from 'services/iniciativa-action.service';
import { clearRegistrationUser } from 'utils/fns';
import { uploadFile } from 'services/file.service';
import { TeamData, Team } from 'types/realization';

const threeSteps = [
  { icon: BsCameraVideoFill as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 1, activeColor: 'bg-green-bb-2', iconColor: 'text-pink-bb-1' },
  { icon: HiUsers as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 2, activeColor: 'bg-green-bb-2', iconColor: 'text-pink-bb-1' },
  { icon: IoCheckmarkCircle as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 3, activeColor: 'bg-green-bb-2', iconColor: 'text-pink-bb-1' },
];

const twoSteps = [
  { icon: BsCameraVideoFill as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 1, activeColor: 'bg-green-bb-2', iconColor: 'text-pink-bb-1' },
  { icon: IoCheckmarkCircle as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 2, activeColor: 'bg-green-bb-2', iconColor: 'text-pink-bb-1' },
];

export function InscricaoIniciativaPremio() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const rule = useRule();
  const { showModal, closeModal } = useModal();

  const iniciativa = (location.state as any)?.iniciativa || null;

  const [currentStep, setCurrentStep] = useState(1);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoEmbedUrl, setVideoEmbedUrl] = useState('');
  const [ruleUrl, setRuleUrl] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<TeamData[]>([]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  useEffect(() => {
    if (rule) setRuleUrl(rule.url);
  }, [rule]);

  // Converte participantes da iniciativa para TeamData para o componente Participants
  const teamData: TeamData[] = useMemo(() => {
    if (!iniciativa?.participantes) return [];
    const target = iniciativa.iniciativaOriginal?.target || [];
    const targetById = new Map<string, any>();
    target.forEach((t: any) => {
      const id = t.extra?.id || t._id?.replace(/^F/, '');
      if (id) targetById.set(id, t);
    });

    return iniciativa.participantes.map((p: any) => {
      const t = targetById.get(p.matricula);
      return {
        _id: p.matricula,
        nome: t?.extra?.nome_guerra || p.nome,
        chave: `F${p.matricula}`,
        nome_guerra: t?.extra?.nome_guerra || p.nome.split(' ')[0],
        funcao: t?.extra?.funcao || '',
        prefixo: t?.extra?.prefixo || '',
        nome_dependencia: t?.extra?.nome_dependencia || '',
        searchField: `${p.matricula} F${p.matricula} ${p.nome} ${t?.extra?.nome_guerra || ''}`,
      } as TeamData;
    });
  }, [iniciativa]);

  // Determina se precisa do step de indicação (> 5 participantes)
  const needsIndication = useMemo(() => {
    if (!iniciativa) return false;
    const participantCount = iniciativa.participantes?.length
      || iniciativa.iniciativaOriginal?.team?.length
      || 0;
    return participantCount > 5;
  }, [iniciativa]);

  const steps = needsIndication ? threeSteps : twoSteps;

  const teamForParticipants: Team[] = useMemo(() => {
    const team = iniciativa?.iniciativaOriginal?.team;
    if (team) {
      return team.map((m: any) => ({
        user_id: m._id,
        message: m.message || '',
      }));
    }
    // Fallback: usa participantes formatados
    if (!iniciativa?.participantes) return [];
    return iniciativa.participantes.map((p: any) => ({
      user_id: p.matricula,
      message: '',
    }));
  }, [iniciativa]);

  const handlerSend = async () => {
    if (isSending) return;
    setIsSending(true);
    try {
      closeModal();
      if (!user || !iniciativa) return;

      const documento: any[] = [];
      if (videoUrl) {
        documento.push({ file: videoUrl, embedUrl: videoEmbedUrl || videoUrl, type: 'video' });
      }
      if (pdfFile) {
        const fileData = await uploadFile(pdfFile);
        documento.push({ file: fileData.url, nameFile: fileData.filename, type: 'file' });
      }

      const nomeGuerra = user.extra?.nome_guerra || user.name;
      const prefixo = user.detail?.prefixo || '';
      const inscricaoPlayer = `${nomeGuerra} (${user._id} - ${prefixo || '-'})`;

      const obj = {
        ...iniciativa.iniciativaOriginal,
      }

      delete obj.log;
      delete obj.m;
      delete obj.p;
      delete obj.target;
      delete obj.confirmedPlayers;

      await atualizarIniciativa({
        ...obj,
        documento,
        dt_inscricao: new Date(),
        status: 'inscrita',
        inscricao_player: inscricaoPlayer,
      });


      const currentPlayer = clearRegistrationUser(user._id);
      const body = {
        id_realizacao: id,
        title: iniciativa.titulo,
        dt_aceite: new Date(),
        rawPlayer: user._id,
        player: currentPlayer,
        player_name: user.name,
        documento,
      };

      await inscricaoIniciativaPremio(body, iniciativa.iniciativaOriginal, selectedParticipants, teamData);

      try {
        // A rota sempre traz o :id da iniciativa.
        await registrarEnviarVideo(user._id, id!);
        await registrarConfirmar(user._id, id!);
      } catch (actionLogError) {
        console.warn('Falha ao registrar action_log da inscrição do prêmio:', actionLogError);
      }

      navigate('/confirmacao-premio');
    } catch (error) {
      console.error('Erro ao finalizar inscrição Premio:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleProximaEtapa = () => {
    if (currentStep === 1) {
      if (needsIndication) {
        setCurrentStep(2);
      } else {
        showTermsModal();
      }
    } else if (currentStep === 2) {
      showTermsModal();
    }
  };

  const showTermsModal = () => {
    showModal(
      'accept-terms',
      <ModalWarning
        rule={ruleUrl}
        title="Sua inscrição está quase pronta!"
        subTitle="Confirme abaixo algumas informações importantes antes de finalizá-la."
        needInfo={true}
        needRule={true}
        description={
          <div className="flex flex-col gap-3 text-base">
            <p>Declaro que li e concordo com os termos estabelecidos na{' '}
              <a href={ruleUrl} target="_blank" rel="noopener noreferrer" className="underline text-blue-bb-1 font-semibold hover:no-underline">403-1, item 7.2</a>.
            </p>
            <p>
              Estou ciente sobre os prazos de inscrição, requisitos de participação, critérios de avaliação e os meus direitos e deveres como participante do Prêmio BB.
            </p>
            <p>Comprometo-me a fornecer informações precisas e completas, além de seguir todas as regras e regulamentos da premiação.</p>
            <p>Autorizo a organização a usar minha imagem em fotos ou vídeos, bem como meus dados pessoais, para fins de divulgação.</p>
            <p>
              Tenho conhecimento de que será realizada a verificação dos requisitos de impedimentos por alguma das situações listadas na{' '}
              <a href={ruleUrl} target="_blank" rel="noopener noreferrer" className="underline text-blue-bb-1 font-semibold hover:no-underline">IN 403-1, item 7.2.8.2</a>,
              com base na situação apurada no primeiro dia útil após o encerramento das inscrições.
            </p>
          </div>
        }
        btnText="Finalizar inscrição"
        onBackButton={closeModal}
        hasBackButton={true}
        onClose={handlerSend}
      />,
      'accept-terms'
    );
  };

  const handleVoltar = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(-1);
    }
  };

  const isNextDisabledStep1 = !videoUrl;
  const isNextDisabledStep2 = selectedParticipants.length < 5;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header currentPage="iniciativas" />

      {/* Step indicator */}
      <div className="bg-gray-100 py-6 mt-8">
        <div className="max-w-[1200px] mx-auto px-4">
          <StepIndicator currentStep={currentStep} steps={steps} progressColor="bg-blue-bb-6" />
        </div>
      </div>

      <main className="px-4 py-8 pb-16">
        <div className="max-w-[1200px] mx-auto">

          {/* Step 1 - Vídeo */}
          {currentStep === 1 && (
            <>
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">
                Anexe o vídeo sobre a iniciativa.
              </h2>

              <div className="flex items-center gap-4 border border-pink-bb-2 rounded-xl p-4 mb-8  relative overflow-hidden">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center">
                  <BsCameraVideoFill className="text-pink-bb w-20 h-20 absolute -left-8" />
                </div>
                <p className="text-base text-gray-600 leading-relaxed">
                 Para seguir para a próxima etapa, é obrigatório anexar um vídeo pitch de no máximo 3 minutos de duração, respondendo a seguinte pergunta norteadora: "Por que essa iniciativa foi fundamental para o cumprimento do direcionador estratégico ao qual está vinculado e quais resultados foram obtidos?  Copie o link do vídeo e cole no campo abaixo. <br/>
                 O anexo de um documento em formato PDF é opcional e pode complementar a apresentação da iniciativa.
                </p>
              </div>

              <div className="mb-10">
                <p className="text-sm font-semibold text-gray-700 mb-3">Incorporar link de vídeo</p>
                <Attachment
                  onFileSelect={(file) => setPdfFile(file)}
                  onVideoSelect={setVideoUrl}
                  onEmbedVideoSelect={setVideoEmbedUrl}
                  onProgressComplete={() => {}}
                  allowedTypes={['application/pdf']}
                  maxFizeSize={3}
                />
              </div>

              <div className="flex items-center justify-end gap-4">
                <button
                  className="bg-transparent border border-gray-300 text-gray-600 rounded-lg px-8 py-3 text-sm font-bold cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => navigate(-1)}
                >
                  VOLTAR
                </button>
                <button
                  disabled={isNextDisabledStep1 || isSending}
                  onClick={handleProximaEtapa}
                  className="bg-[#f5c518] text-[#1e3a5f] border-0 rounded-lg px-8 py-3 text-sm font-extrabold cursor-pointer hover:bg-[#e6b800] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  IR PARA A PRÓXIMA ETAPA
                </button>
              </div>
            </>
          )}

          {/* Step 2 - Indicação de participantes (condicional) */}
          {currentStep === 2 && needsIndication && (
            <>
            <h2 className="text-2xl md:text-[32px] font-bold text-gray-bb-1 mb-6 font-title leading-tight">
             Abaixo, indique os 5 participantes que mais atuaram para o sucesso da entrega desta iniciativa. Caso a iniciativa seja premiada na última etapa de avaliação, os 5 poderão ser contemplados com o prêmio individual.
            </h2>

            <div className="flex items-center gap-4 border border-pink-bb-2 rounded-xl p-4 mb-8 relative overflow-hidden">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center">
                <FaBell className="text-pink-bb w-20 h-20 absolute -left-6" />
              </div>
              <p className="text-base text-gray-600 leading-relaxed">
                  Durante o período de inscrição, os funcionários que estiverem em "Situação Diferente de 100 – Força de Trabalho Real" devem confirmar a participação no Prêmio e a adesão ao Regulamento da premiação entrando em contato com a Gepes Atendimento pelos telefones: 4003-5291 (Capitais e regiões metropolitanas) ou 0800 881 5291 (demais localidades).
                </p>
            </div>
            <Participants
              options={teamData}
              limitSelected={5}
              minSelected={5}
              onSelected={(val) => setSelectedParticipants(val || [])}
              onBack={handleVoltar}
              onNext={handleProximaEtapa}
              isNextDisabled={isNextDisabledStep2}
              team={teamForParticipants}
            />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
